/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {
    Box,
    StylesProvider,
    useMultiStyleConfig,
    HStack,
    Flex,
    useStyles,
    Link,
    Image,
    useMediaQuery
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {usePage} from '@salesforce/commerce-sdk-react'
import LinksList from '@consteed/overrides/components/links-list'
import {VisaIcon, MastercardIcon, PaypalIcon} from '@consteed/overrides/components/icons'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

const CheckoutFooter = ({...otherProps}) => {
    const styles = useMultiStyleConfig('CheckoutFooter')
    const {data: page} = usePage({parameters: {pageId: 'footer-checkout'}})
    const [firstLinkColumn, setFirstLinkColumn] = React.useState([])
    const [largerThan767] = useMediaQuery('(min-width: 768px)')

    React.useEffect(() => {
        if (page?.regions) {
            const firstRegion = page.regions.find((region) => {
                return region.id === 'first'
            })

            const convertLinkRegion = (region) => {
                return region?.components?.[0]?.regions?.[0]?.components
                    ?.map((link) => {
                        let linkListElement = {
                            text: link?.data?.linkText
                        }
                        switch (link?.data?.linkType) {
                            case 'Page':
                                if (!link?.data?.page) {
                                    return null
                                }
                                linkListElement.href = '/page-viewer/' + link?.data?.page
                                break
                            default:
                                return null
                        }
                        return linkListElement
                    })
                    .filter((linkListElement) => linkListElement)
            }

            if (firstRegion) {
                const firstColumnLinks = convertLinkRegion(firstRegion)
                if (firstColumnLinks) {
                    setFirstLinkColumn(firstColumnLinks)
                }
            }
        }
    }, [page])
    return (
        <Box as="footer" {...styles.container} {...otherProps}>
            <Box {...styles.content}>
                <StylesProvider value={styles}>
                    <Flex
                        flexDirection={{base: 'column', lg: 'row'}}
                        justifyContent={{lg: 'space-between'}}
                        alignItems={{lg: 'center'}}
                        mb={{lg: 10}}
                    >
                        <Box mb={{base: 10, lg: 0}}>
                            <Link href={'./'}>
                                <Image
                                    src={getAssetUrl('static/img/global/logoOrangeConsteed.png')}
                                    alt={'Consteed Logo'}
                                />
                            </Link>
                        </Box>
                        <LinksList
                            {...styles.linksList}
                            links={firstLinkColumn}
                            linkTextStyle={{
                                base: 'bookSmall',
                                md: 'bookLarge',
                                xl: 'bookXlarge'
                            }}
                            variant={largerThan767 ? 'horizontal' : 'vertical'}
                        />
                    </Flex>
                    <Box>
                        <CreditCardIcons />
                    </Box>
                </StylesProvider>
            </Box>
        </Box>
    )
}

export default CheckoutFooter

const CreditCardIcons = (props) => {
    const styles = useStyles()
    return (
        <HStack sizing={2} {...styles.paymentIconsContainer} {...props}>
            <VisaIcon {...styles.creditCardIcon} />
            <MastercardIcon {...styles.creditCardIcon} />
            <PaypalIcon {...styles.creditCardIcon} />
        </HStack>
    )
}
